import { FunctionComponent, useEffect, useRef } from 'react';
import styled from 'styled-components';

const ScreenReaderOnly = styled('span')`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

// Reveals a line word by word in step with the scroll position: words fade in while the
// line travels from near the bottom of the screen to just above the middle, and fade back
// out when scrolling up. With reduced motion the whole line simply shows.
// Ported from the website's homepage (cozyjuicyreal/src/pages/Index.tsx).
export const RevealLine: FunctionComponent<{ text: string; className?: string }> = ({
  text,
  className,
}) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const words = text.split(' ');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const wordEls = Array.from(el.querySelectorAll<HTMLSpanElement>('[data-word]'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const paint = (progress: number) => {
      wordEls.forEach((w, i) => {
        // Each word fades over 1.5 "slots" so neighbours overlap a little.
        const t = Math.min(1, Math.max(0, (progress * (wordEls.length + 0.5) - i) / 1.5));
        w.style.opacity = String(t);
        w.style.transform = `translateY(${(1 - t) * 14}px)`;
        w.style.filter = t < 1 ? `blur(${(1 - t) * 4}px)` : 'none';
      });
    };

    if (reduceMotion) {
      paint(1);
      return;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * 0.9; // line's centre near the bottom of the screen: nothing shown
      const end = vh * 0.4; // line's centre just above the middle: fully shown
      const centre = rect.top + rect.height / 2;
      paint(Math.min(1, Math.max(0, (start - centre) / (start - end))));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <p ref={ref} className={className}>
      <ScreenReaderOnly>{text}</ScreenReaderOnly>
      <span aria-hidden="true">
        {words.map((word, i) => (
          <span key={i}>
            <span data-word style={{ display: 'inline-block', willChange: 'opacity, transform' }}>
              {word}
            </span>
            {i < words.length - 1 && ' '}
          </span>
        ))}
      </span>
    </p>
  );
};
